import React, {useCallback, useEffect, useRef, useState} from 'react'
import {
  FaExclamationCircle,
  FaFileAlt,
  FaCheckCircle,
  FaPaperclip,
  FaCommentDots,
  FaCloudUploadAlt,
  FaTimes,
  FaTrash,
  FaExclamationTriangle,
} from 'react-icons/fa'
import axios from 'axios'
import '../../layout-inpatient/style.css'
import ChatModal from '../../layout-inpatient/tab-components/ChatModal'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {createPusherClient} from '../../../../_metronic/helpers/pusherClient'

interface SettlementDocumentProps {
  claimDetails: any
  onDocumentClick: (docLabel: string) => void
  onDocumentsStatusChange: (status: boolean) => void
  onDocumentValidationChange?: (hasInvalid: boolean) => void
  onDocumentRealtimeUpdate?: (data: any) => void
}

const SettlementDocument: React.FC<SettlementDocumentProps> = ({
  claimDetails,
  onDocumentClick,
  onDocumentsStatusChange,
  onDocumentValidationChange,
  onDocumentRealtimeUpdate,
}) => {
  const user = useAuth()
  const {auth} = useAuth()
  console.log('Token Documents: ', user.currentUser?.token)
  const [documentFiles, setDocumentFiles] = useState<Record<string, any[]>>({})
  const [uploadStatus, setUploadStatus] = useState<Record<string, boolean | null>>({})
  const [documentsData, setDocumentsData] = useState<any[]>([])
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false)
  const [chatMessage, setChatMessage] = useState<string>('')
  const [activeOptionsModal, setActiveOptionsModal] = useState<{
    key: number
    label: string
    mode?: 'replace' | 'delete'
  } | null>(null)
  const [deletingFiles, setDeletingFiles] = useState<Record<string, boolean>>({})
  const [lastToastTime, setLastToastTime] = useState<number>(0)
  const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false)

  // Mirrors for the Pusher handler to read. The handler both reads and sets these,
  // so keeping them in the Pusher effect's deps rebuilt the client on every file
  // event. Refs give the handler the same committed value without the churn.
  // See docs/plan/IDH-1620.
  const documentFilesRef = useRef<Record<string, any[]>>(documentFiles)
  const lastToastTimeRef = useRef<number>(lastToastTime)
  useEffect(() => {
    documentFilesRef.current = documentFiles
  }, [documentFiles])
  useEffect(() => {
    lastToastTimeRef.current = lastToastTime
  }, [lastToastTime])

  const defaultDocuments = [{label: 'Documents Settlement', key: 0, status: 'Default'}]

  useEffect(() => {
    const initialFiles: Record<string, any[]> = {}
    defaultDocuments.forEach((doc) => {
      initialFiles[doc.label] = []
    })
    setDocumentFiles(initialFiles)
  }, [])

  const checkAllDocumentsUploaded = useCallback(() => {
    // Get all unique document labels from both default and API response
    const allDocumentLabels = new Set([
      ...defaultDocuments.map((doc) => doc.label),
      ...Object.keys(documentFiles),
    ])

    const allUploaded = Array.from(allDocumentLabels).every((label) => {
      // Skip Prescription documents
      if (label === 'Prescription') return true

      const files = documentFiles[label]
      return files && files.length > 0 && !files.some((file) => file.status === 'INVALID')
    })

    onDocumentsStatusChange(allUploaded)
  }, [documentFiles, onDocumentsStatusChange])

  useEffect(() => {
    checkAllDocumentsUploaded()
  }, [documentFiles, checkAllDocumentsUploaded])

  /**
   * ListFiles can take many seconds (IDH-1619 — svc.claim awaits a Pusher publish
   * inside the GET). Until it returns, the rows fall back to `defaultDocuments`
   * and read as "no file uploaded" with a live Upload button, which invites a
   * duplicate upload. Pass `background: true` to refresh without the indicator.
   */
  const fetchDocuments = async ({background = false}: {background?: boolean} = {}) => {
    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }

    if (!background) setIsLoadingDocuments(true)

    try {
      const response = await axios.get(apiUrl, {headers})
      if (response.data.isSuccess) {
        const apiDocuments = response.data.data.filter(
          (doc: any) => doc.label === 'Documents Settlement'
        )

        // Check for invalid documents across all documents
        const hasInvalidDocuments = apiDocuments.some(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.some((file: any) => file.status === 'INVALID')
        )

        // Create a map of all unique documents
        const allDocuments = new Map()

        // Add default documents first
        defaultDocuments.forEach((doc) => {
          allDocuments.set(doc.label, {...doc, key: doc.key})
        })

        // Add or update with API documents
        apiDocuments.forEach((doc: any, index: number) => {
          if (!allDocuments.has(doc.label)) {
            // For new documents not in default list, create a new key
            allDocuments.set(doc.label, {
              label: doc.label,
              key: defaultDocuments.length + index,
              document: doc.document,
            })
          } else {
            // Update existing document with API data
            const existing = allDocuments.get(doc.label)
            allDocuments.set(doc.label, {
              ...existing,
              document: doc.document,
            })
          }
        })

        // Convert map back to array
        const finalDocuments = Array.from(allDocuments.values())

        // Create updated files object
        const updatedFiles: Record<string, any[]> = {}
        finalDocuments.forEach((doc: any) => {
          if (doc.document) {
            updatedFiles[doc.label] = doc.document.map((file: any) => ({
              name: file.name,
              id: file.id,
              type: file.name.split('.').pop(),
              status: file.status,
              isArchive: file.isArchive,
            }))
          } else {
            updatedFiles[doc.label] = []
          }
        })

        setDocumentsData(finalDocuments)
        setDocumentFiles(updatedFiles)

        // Update parent components
        const allDocumentsValid = !hasInvalidDocuments
        onDocumentsStatusChange(allDocumentsValid)
        if (onDocumentValidationChange) {
          onDocumentValidationChange(hasInvalidDocuments)
        }
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
      toast.error('Failed to fetch documents. Please try again.', {
        position: 'bottom-right',
        autoClose: 3000,
      })
    } finally {
      if (!background) setIsLoadingDocuments(false)
    }
  }

  useEffect(() => {
    fetchDocuments()
  }, [claimDetails])

  useEffect(() => {
    console.log('Token in useEffect:', user)
  }, [user])

  useEffect(() => {
    const pusher = createPusherClient('SettlementDocument')

    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    const fileStatusChannel = pusher.subscribe(`file-channel-${claimNumber}`)
    const listFileChannel = pusher.subscribe(`list-file-channel-${claimNumber}`)

    fileStatusChannel.bind(`get-${claimNumber}`, (data: any) => {
      if (data && data.message) {
        const {message} = data
        const {Label, Status, Id, DocumentName, isArchive, UpdatedBy} = message

        // Update document files state immediately
        setDocumentFiles((prevFiles) => {
          const newFiles = {...prevFiles}
          if (newFiles[Label]) {
            newFiles[Label] = [
              {
                name: DocumentName,
                id: Id,
                type: DocumentName.split('.').pop(),
                status: Status,
                isArchive: isArchive,
              },
            ]
          }
          return newFiles
        })

        // Update documents data state
        setDocumentsData((prevData) => {
          return prevData.map((doc) => {
            if (doc.label === Label) {
              return {
                ...doc,
                document: [
                  {
                    name: DocumentName,
                    id: Id,
                    status: Status,
                    isArchive: isArchive,
                  },
                ],
              }
            }
            return doc
          })
        })

        // Check for any invalid documents
        const hasInvalidDocuments = Object.values(documentFilesRef.current).some((files) =>
          files.some((file) => file.status === 'INVALID')
        )

        // Check if all required documents are uploaded
        const allDocumentsUploaded = defaultDocuments.every(
          (doc) => documentFilesRef.current[doc.label]?.length > 0
        )

        // Update parent components
        onDocumentsStatusChange(allDocumentsUploaded && !hasInvalidDocuments)
        if (onDocumentValidationChange) {
          onDocumentValidationChange(hasInvalidDocuments)
        }

        // Show toast for invalid documents
        const now = Date.now()
        if (Status === 'INVALID' && now - lastToastTimeRef.current > 5000) {
          toast.error(`${Label} has been rejected by ${UpdatedBy}. Please review and resubmit.`, {
            position: 'top-right',
            autoClose: 3000,
          })
          setLastToastTime(now)
        }
      }
    })

    listFileChannel.bind(`get-list-${claimNumber}`, (data: any) => {
      console.log('Received list file data:', data)

      if (data.isSuccess) {
        // Pass the realtime data up to parent
        if (onDocumentRealtimeUpdate) {
          onDocumentRealtimeUpdate(data)
        }

        const apiDocuments = data.data.filter((doc: any) => doc.label !== 'Prescription')

        // Check for invalid documents
        const hasInvalidDocuments = apiDocuments.some(
          (doc: any) =>
            doc.document &&
            doc.document.length > 0 &&
            doc.document.some((file: any) => file.status === 'INVALID')
        )

        // Check if all required documents are present
        const allDocumentsUploaded = defaultDocuments.every((defaultDoc) =>
          apiDocuments.some(
            (apiDoc) =>
              apiDoc.label === defaultDoc.label && apiDoc.document && apiDoc.document.length > 0
          )
        )

        // Create maps for easier comparison
        const defaultDocsMap = new Map(defaultDocuments.map((doc) => [doc.label, doc]))
        const apiDocsMap = new Map(apiDocuments.map((doc: any) => [doc.label, doc]))

        // Determine which set of documents to use based on our strategy
        let finalDocuments: any[] = []

        // Case 1: API count equals Default count - Compare and use API as source of truth
        if (apiDocuments.length === defaultDocuments.length) {
          finalDocuments = defaultDocuments.map((defaultDoc) => {
            const apiDoc = apiDocsMap.get(defaultDoc.label)
            return apiDoc || defaultDoc
          })
        }
        // Case 2: API count > Default count - Use API data
        else if (apiDocuments.length > defaultDocuments.length) {
          finalDocuments = apiDocuments
        }
        // Case 3: Default count > API count - Use Default data but update with available API data
        else {
          finalDocuments = defaultDocuments.map((defaultDoc) => {
            const apiDoc = apiDocsMap.get(defaultDoc.label)
            return apiDoc || defaultDoc
          })
        }

        // Create updated files object based on final documents
        const updatedFiles: Record<string, any[]> = {}

        finalDocuments.forEach((doc: any) => {
          // If it's an API document with files
          if (doc.document) {
            updatedFiles[doc.label] = doc.document.map((file: any) => ({
              name: file.name,
              id: file.id,
              type: file.name.split('.').pop(),
              status: file.status,
              isArchive: file.isArchive,
            }))
          }
          // If it's a default document or empty API document
          else {
            updatedFiles[doc.label] = []
          }
        })

        setDocumentsData(finalDocuments)
        setDocumentFiles(updatedFiles)

        // Notify parent components of status changes
        onDocumentsStatusChange(allDocumentsUploaded)
        if (onDocumentValidationChange) {
          onDocumentValidationChange(hasInvalidDocuments)
        }
      }
    })

    return () => {
      fileStatusChannel.unbind_all()
      pusher.unsubscribe(`file-channel-${claimNumber}`)
      listFileChannel.unbind_all()
      pusher.unsubscribe(`list-file-channel-${claimNumber}`)
      pusher.disconnect()
    }
  }, [claimDetails.claimDetails.claim.claimNumber])

  const status = claimDetails?.claimDetails?.claim?.status

  console.log('Testa : ', documentsData)

  // Add this function to check if interactions should be disabled
  const isInteractionDisabled = () => {
    const disabledStatuses = [
      'Request IGL',
      'IGL RELEASED',
      'Daily Monitoring',
      'Request FGL',
      //   'FGL RELEASED'
    ]
    // Also disabled while ListFiles is in flight — the current file state is
    // unknown until it returns, so uploading now risks a duplicate.
    return isLoadingDocuments || disabledStatuses.includes(status)
  }

  // Modify handleFileChange to check status first
  const handleFileChange = async (label: string, event: React.ChangeEvent<HTMLInputElement>) => {
    if (isInteractionDisabled()) return

    if (event.target.files) {
      const selectedFiles = Array.from(event.target.files)

      // Check file size limit (10MB) BEFORE updating any state
      const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
      const oversizedFiles = selectedFiles.filter((file) => file.size > MAX_FILE_SIZE)

      if (oversizedFiles.length > 0) {
        const fileNames = oversizedFiles.map((f) => f.name).join(', ')
        toast.error(`File size exceeds maximum allowed size of 10MB: ${fileNames}`, {
          position: 'bottom-right',
          autoClose: 5000,
        })
        // Reset the file input
        event.target.value = ''
        return
      }

      try {
        const currentDoc = documentsData.find((doc) => doc.label === label)
        const currentFileId = currentDoc?.document?.[0]?.id

        // Immediately update UI to show IDLE status
        setDocumentFiles((prevState) => ({
          ...prevState,
          [label]: [
            {
              name: selectedFiles[0].name || '',
              type: selectedFiles[0].name.split('.').pop() || '',
              status: 'IDLE',
              id: currentFileId || 0,
            },
          ],
        }))

        // Also update documentsData for immediate UI reflection
        setDocumentsData((prevData) =>
          prevData.map((doc) => {
            if (doc.label === label) {
              return {
                ...doc,
                document: [
                  {
                    name: selectedFiles[0].name,
                    id: currentFileId || 0,
                    status: 'IDLE',
                    isArchive: false,
                  },
                ],
              }
            }
            return doc
          })
        )

        // Check document statuses immediately after UI update
        const hasInvalidDocuments = Object.values(documentFiles).some((files) =>
          files.some((file) => file.status === 'INVALID')
        )

        // Update parent components
        onDocumentsStatusChange(!hasInvalidDocuments)
        if (onDocumentValidationChange) {
          onDocumentValidationChange(hasInvalidDocuments)
        }

        // Attempt upload
        await uploadFiles(label, selectedFiles, currentFileId)
      } catch (error) {
        console.error('Error handling file change:', error)
        toast.error('Failed to upload file. Please try again.', {
          position: 'bottom-right',
          autoClose: 3000,
        })
      }
    }
  }

  const uploadFiles = async (label: string, files: File[], existingFileId?: number) => {
    if (!files || files.length === 0) return

    // Check file size limit (10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE)

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((f) => f.name).join(', ')
      toast.error(`File size exceeds maximum allowed size of 10MB: ${fileNames}`, {
        position: 'bottom-right',
        autoClose: 5000,
      })
      return
    }

    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))

    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    const typeDocument = label

    // Always use the existing file ID if available
    const documentId = existingFileId || 0
    console.log('Using Document ID for upload:', documentId) // Debug log

    const apiUrl = `${
      process.env.REACT_APP_API_CLAIM_URL
    }/api/Claims/UploadFiles?claimNumber=${claimNumber}&typeDocument=${encodeURIComponent(
      typeDocument
    )}&user=${user.currentUser?.email}&id=${documentId}`

    try {
      setUploadStatus((prevStatus) => ({...prevStatus, [label]: null}))

      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${user.currentUser?.token}`,
        },
      })

      if (response.data.isSuccess) {
        // After successful upload, fetch updated document list. Background: the row
        // already shows the optimistic IDLE file, so the foreground indicator would
        // blank the grid the staff just uploaded into for the length of ListFiles.
        await fetchDocuments({background: true})
        setUploadStatus((prevStatus) => ({...prevStatus, [label]: true}))
        toast.success('File uploaded successfully!', {
          position: 'bottom-right',
          autoClose: 3000,
        })
      } else {
        throw new Error(response.data.message || 'Upload failed')
      }
    } catch (error) {
      console.error('Error uploading files:', error)
      setUploadStatus((prevStatus) => ({...prevStatus, [label]: false}))
      throw error
    }
  }

  console.log('Token Documents: ', user.currentUser?.token)

  const handleDeleteFile = async (file: any, docKey: number) => {
    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/DeleteFile?id=${file.id}&claimNumber=${claimNumber}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }

    try {
      setDeletingFiles((prev) => ({...prev, [file.id]: true}))

      const response = await axios.post(apiUrl, {}, {headers})

      // Check if response contains "DELETED FILE SUCCESS" message
      if (response.data === 'DELETED FILE SUCCESS.' || response.data.isSuccess) {
        // Update the documentFiles state
        setDocumentFiles((prev) => {
          const newFiles = {...prev}
          const docLabel = defaultDocuments.find((doc) => doc.key === docKey)?.label
          if (docLabel) {
            newFiles[docLabel] = []
          }
          return newFiles
        })

        setDocumentsData((prevData) =>
          prevData.map((doc) => {
            if (doc.key === docKey) {
              return {
                ...doc,
                document: [],
              }
            }
            return doc
          })
        )

        toast.success('File deleted successfully!', {
          position: 'bottom-right',
          autoClose: 3000,
        })

        // Background: the row was already cleared optimistically above.
        await fetchDocuments({background: true})
      } else {
        throw new Error('Delete operation failed')
      }
    } catch (error) {
      console.error('Error deleting file:', error)
      // Only show error toast if it's a real error
      if (error instanceof Error && error.message !== 'DELETED FILE SUCCESS.') {
        toast.error('Error deleting file', {
          position: 'bottom-right',
          autoClose: 3000,
        })
      }
    } finally {
      setDeletingFiles((prev) => {
        const newState = {...prev}
        delete newState[file.id]
        return newState
      })
    }
  }

  // Modify openOptionsModal to check status first
  const openOptionsModal = (doc: {label: string; key: number}) => {
    if (isInteractionDisabled()) return

    // Find the current document
    const currentDoc = documentsData.find((d) => d.label === doc.label)?.document?.[0]

    // Don't open modal if document status is VALID
    if (currentDoc?.status === 'VALID') return

    setActiveOptionsModal(doc)
  }

  const closeOptionsModal = () => {
    setActiveOptionsModal(null)
  }

  const handleSendToChat = () => {
    if (activeOptionsModal) {
      setChatMessage(`${activeOptionsModal.label}: Discuss document details`)
      setIsChatOpen(true)
      closeOptionsModal()
    }
  }

  const handleReUpload = () => {
    if (activeOptionsModal) {
      const fileInput = document.getElementById(
        `upload-${activeOptionsModal.label}`
      ) as HTMLInputElement
      if (fileInput) {
        // Find the current document
        const currentDoc = documentsData.find((doc) => doc.label === activeOptionsModal.label)
        console.log('currentDoc', currentDoc)

        if (currentDoc?.document && Array.isArray(currentDoc.document)) {
          // Find the first file with INVALID or Request Document status
          const invalidFile = currentDoc.document.find(
            (file) =>
              file.status === 'INVALID' ||
              file.status === 'Invalid' ||
              file.status === 'Request Document'
          )

          console.log('Found invalid file:', invalidFile)

          if (invalidFile) {
            // Store the file ID for use during upload
            fileInput.dataset.replaceFileId = invalidFile.id.toString()
            console.log('Setting replaceFileId to:', invalidFile.id)
          } else {
            // If no invalid file found, use the first file as fallback
            const firstFile = currentDoc.document[0]
            if (firstFile) {
              fileInput.dataset.replaceFileId = firstFile.id.toString()
              console.log('No invalid file found, using first file ID:', firstFile.id)
            }
          }
        }

        fileInput.click()
        closeOptionsModal()
      }
    }
  }

  const handleFileReplace = (docLabel: string, fileToReplace: any) => {
    const fileInput = document.getElementById(`upload-${docLabel}`) as HTMLInputElement
    if (fileInput) {
      if (fileToReplace?.id) {
        fileInput.dataset.replaceFileId = fileToReplace.id.toString()
      }
      fileInput.click()
    }
  }

  const getDocumentStatusIcon = (document: any) => {
    if (!document || !document.name) {
      return <FaExclamationCircle className='document-icon warning' />
    }

    switch (document.status) {
      case 'VALID':
        return <FaCheckCircle className='document-icon success' />
      case 'REQUEST':
        return <FaExclamationTriangle className='document-icon request' />
      default:
        return <FaExclamationCircle className='document-icon warning' />
    }
  }

  const getDocumentStatusText = (document: any) => {
    if (!document || !document.name) {
      return 'Document not uploaded'
    }

    switch (document.status) {
      case 'VALID':
        return 'Approved'
      case 'INVALID':
        return 'Revision needed'
      default:
        return 'Pending'
    }
  }

  return (
    <>
      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
        style={{zIndex: 9999}}
      />
      <div className='documents-container'>
        {isLoadingDocuments && (
          <div
            className='d-flex align-items-center justify-content-center gap-2 py-4'
            style={{gridColumn: '1 / -1'}}
            role='status'
            aria-live='polite'
          >
            <div className='spinner-border spinner-border-sm text-primary' />
            <span className='text-primary fw-bold' style={{fontSize: '12px'}}>
              Loading documents...
            </span>
          </div>
        )}
        {(isLoadingDocuments
          ? []
          : documentsData.length > 0
          ? documentsData
          : defaultDocuments
        ).map((doc, index) => {
          const currentDoc = doc.document?.[0]

          return (
            <div
              className={`document-item ${activeOptionsModal?.label === doc.label ? 'active' : ''}`}
              key={doc.label}
            >
              <div
                className='document-header'
                onClick={() => {
                  console.log('Document clicked:', doc.label) // Debug log
                  if (isInteractionDisabled()) return

                  const currentDoc = doc.document?.[0]

                  // If document is VALID, don't do anything
                  if (currentDoc?.status === 'VALID') return

                  // For documents that need upload or have REQUEST status
                  if (!currentDoc?.name || currentDoc?.status === 'REQUEST') {
                    const fileInput = document.getElementById(
                      `upload-${doc.label}`
                    ) as HTMLInputElement
                    if (fileInput) {
                      fileInput.click()
                    }
                  } else {
                    openOptionsModal(doc)
                  }
                }}
                style={{
                  cursor:
                    isInteractionDisabled() || doc.document?.[0]?.status === 'VALID'
                      ? 'default'
                      : 'pointer',
                }}
              >
                <div className='document-header-content'>
                  {getDocumentStatusIcon(currentDoc)}
                  <span className='document-text'>{doc.label}</span>
                  {currentDoc?.status === 'REQUEST' && (
                    <span className='document-status request'>Revision needed</span>
                  )}
                </div>
              </div>

              {currentDoc?.name && (
                <div className='document-files-list'>
                  <div className='document-file-item'>
                    {currentDoc.type === 'pdf' ? (
                      <FaFileAlt className='file-icon pdf' />
                    ) : (
                      <FaPaperclip className='file-icon' />
                    )}
                    <span className='document-file-name'>{currentDoc.name}</span>
                    <span className={`status-badge ${currentDoc.status.toLowerCase()}`}>
                      {getDocumentStatusText(currentDoc)}
                    </span>
                  </div>
                </div>
              )}

              <input
                id={`upload-${doc.label}`}
                type='file'
                accept='.pdf,.jpg,.jpeg,.png'
                style={{display: 'none'}}
                onChange={(e) => handleFileChange(doc.label, e)}
                disabled={isInteractionDisabled()}
                aria-label={`Upload ${doc.label}`}
              />
            </div>
          )
        })}

        <style>
          {`
            .document-icon {
              margin-right: 10px;
              font-size: 18px;
            }
            
            .document-icon.success {
              color: #50CD89;
            }
            
            .document-icon.warning {
              color: #FFC700;
            }
            
            .document-icon.request {
              color: #F1416C;
            }

            .document-status {
              margin-left: auto;
              font-size: 12px;
              padding: 4px 8px;
              border-radius: 4px;
            }

            .document-status.request {
              color: #F1416C;
              background-color: #FFF5F8;
            }

            .status-badge {
              margin-left: auto;
              padding: 4px 8px;
              border-radius: 4px;
              font-size: 12px;
            }

            .status-badge.valid {
              background-color: #E8FFF3;
              color: #50CD89;
            }

            .status-badge.request {
              background-color: #FFF5F8;
              color: #F1416C;
            }

            .status-badge.idle {
              background-color: #FFF8DD;
              color: #FFC700;
            }
          `}
        </style>

        {activeOptionsModal && !activeOptionsModal.mode && !isInteractionDisabled() && (
          <div className='options-modal-overlay' onClick={closeOptionsModal}>
            <div className='options-modal' onClick={(e) => e.stopPropagation()}>
              <div className='options-modal-header'>
                <h3>{activeOptionsModal.label}</h3>
                <button className='options-modal-close' onClick={closeOptionsModal}>
                  <FaTimes />
                </button>
              </div>
              <div className='options-modal-content'>
                <button className='options-modal-button' onClick={handleReUpload}>
                  <FaCloudUploadAlt className='button-icon' />
                  Re-Upload
                </button>
                {documentsData.find((doc) => doc.label === activeOptionsModal.label)?.document?.[0]
                  ?.status !== 'VALID' && (
                  <button
                    className='options-modal-button delete-button'
                    onClick={() => {
                      const currentDoc = documentsData.find(
                        (doc) => doc.label === activeOptionsModal.label
                      )
                      if (currentDoc?.document?.[0]) {
                        handleDeleteFile(currentDoc.document[0], activeOptionsModal.key)
                        closeOptionsModal()
                      }
                    }}
                  >
                    <FaTrash className='button-icon' />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <ChatModal
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          message={chatMessage}
          claimDetails={claimDetails}
        />
      </div>
    </>
  )
}

export default SettlementDocument
